import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { rebaseRenderedHtml } from '@/api/renderedHtml'
import { SensitiveOverlay } from '@/components/SensitiveImage'
import { sensitiveImageClass, useSensitiveMask } from '@/hooks'
import { cn } from '@/utils/cn'

/**
 * 渲染后端产出的正文 HTML，并让里面的图片点一下就在新标签页打开原图。
 *
 * ## 为什么是新标签页，而不是自己做一个缩放弹层
 *
 * 浏览器打开图片后自带缩放、平移、另存、复制地址，以及中键/⌘-点击后台打开；
 * 自己做的弹层是这套能力的子集，还得自己养焦点管理和移动端手势。
 * 正文图片被 `.prose-modsite img` 压到容器宽度的 1/4，读者点开的诉求就是
 * 「看原图」——那就把原图交给浏览器，与 `Gallery.tsx` 的主图行为保持一致。
 *
 * ## 为什么图片需要事件委托，而不是逐个绑定
 *
 * 内容来自 `dangerouslySetInnerHTML`，图片是原始 DOM 节点，不是 React 元素——
 * 没有 `onClick` 这种东西可以挂。点击事件会从 `<img>` 冒泡到这层容器，
 * 用一个容器级 `onClick` 判断 `event.target` 是不是 `<img>` 就够了，
 * 不需要在渲染后再遍历一遍 DOM 去挂监听器。
 *
 * `tabIndex`/`role`/`aria-label` 例外——那三个是**可访问性**，键盘用户要能
 * `Tab` 到图片再按 Enter，这件事没有对应的事件委托方案，只能在内容变化后
 * 用 `useEffect` 走一遍容器里的 `<img>`，直接改它们的 DOM 属性。
 *
 * ## 敏感图片的遮罩同样只能这么做
 *
 * 正文里哪些图要遮，后端**不写进 HTML**：`rendered_html` 是发布时封存的缓存，
 * 把标记渲染进去就意味着改一次标记要重渲染全部已发布修订。标记走的是详情响应
 * 里的 `gallery`（按定义含有正文里的每一张图），调用方把它换算成一组地址传进来。
 *
 * 于是这里要把覆盖层盖到一批**不是 React 元素**的 `<img>` 上。做法是给每张要遮
 * 的图就地套一个定位容器，再往容器里 `createPortal` 一个真正的 React 覆盖层 ——
 * 覆盖层因此照常有状态、照常能在「确认显示」之后自己消失，而不是一段拿字符串
 * 拼出来、还要自己挂监听器的 DOM。
 */

export interface RenderedHtmlContentProps {
  /** 后端渲染缓存，未经改写的原始 HTML（本组件负责调用 `rebaseRenderedHtml`）。 */
  html: string
  /** 无障碍名称前缀，通常是帖子标题。 */
  title: string
  /**
   * 需要先遮罩的图片地址（`mediaUrl(public_key)` 形式），通常由
   * `toSensitiveImageUrls(detail)` 算出。
   *
   * 缺省 = 不遮任何一张。管理端的草稿预览就是这条路：那里看的是自己正在编辑的
   * 内容，遮起来只会碍事。
   */
  sensitiveUrls?: ReadonlySet<string>
  className?: string
}

export function RenderedHtmlContent({
  html,
  title,
  sensitiveUrls,
  className,
}: RenderedHtmlContentProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rebased = rebaseRenderedHtml(html)
  /** 每张要遮的图：它自己，以及套在它外面那个容器里的挂载点。 */
  const [masks, setMasks] = useState<{ url: string; image: HTMLImageElement; slot: HTMLElement }[]>(
    [],
  )

  // 内容变化后才需要重新打点：正文不会在两次渲染之间被别处改写。
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    for (const image of container.querySelectorAll('img')) {
      // 已经被作者包在链接里的图片交给那个链接，别抢它的点击。
      if (image.closest('a')) continue
      image.tabIndex = 0
      image.setAttribute('role', 'link')
      image.setAttribute(
        'aria-label',
        image.alt ? `在新标签页打开图片：${image.alt}` : `在新标签页打开 ${title} 的图片`,
      )
    }
  }, [rebased, title])

  /*
    给要遮的图就地套一个定位容器。

    只在内容或名单变化时跑：`<img>` 是 `dangerouslySetInnerHTML` 出来的原生节点，
    React 只会在 `rebased` 变化时整段重写它们，那时套好的容器一并消失，所以这里
    每次都要认一遍「这张图是不是已经套过了」。
  */
  useEffect(() => {
    const container = containerRef.current
    if (!container || !sensitiveUrls || sensitiveUrls.size === 0) {
      setMasks([])
      return
    }

    const next: { url: string; image: HTMLImageElement; slot: HTMLElement }[] = []
    for (const image of container.querySelectorAll('img')) {
      // 与 `mediaUrl()` 产出的字符串逐字比较：`rebaseRenderedHtml` 把正文里的
      // 根相对路径改写成的正是同一个函数的结果。取属性原文而不是 `image.src`，
      // 后者是浏览器解析过的绝对地址，永远对不上。
      const src = image.getAttribute('src')
      if (!src || !sensitiveUrls.has(src)) continue

      const parent = image.parentElement
      let wrapper: HTMLElement
      if (parent instanceof HTMLElement && parent.dataset.sensitiveWrap === 'true') {
        wrapper = parent
      } else {
        wrapper = document.createElement('span')
        wrapper.dataset.sensitiveWrap = 'true'
        // `overflow-hidden`：遮罩把图放大了 25%，溢出的部分要裁掉，
        // 否则糊开的边缘会盖住旁边的文字。
        wrapper.className = 'relative inline-block max-w-full overflow-hidden align-bottom'
        image.replaceWith(wrapper)
        wrapper.append(image)
      }

      let slot = wrapper.querySelector<HTMLElement>('[data-sensitive-slot]')
      if (!slot) {
        slot = document.createElement('span')
        slot.dataset.sensitiveSlot = 'true'
        wrapper.append(slot)
      }
      next.push({ url: src, image, slot })
    }
    setMasks(next)
  }, [rebased, sensitiveUrls])

  const openInNewTab = (target: EventTarget | null) => {
    if (!(target instanceof HTMLImageElement)) return false
    if (target.closest('a')) return false
    // 还遮着的图不给开：点它的人此刻要的是「先让我看看这是什么」，
    // 而新标签页里是**没有遮罩的原图**。确认之后再点就照常打开。
    if (target.dataset.sensitiveMasked === 'true') return false
    // `currentSrc` 优先：响应式图片（`srcset`）下 `src` 未必是实际生效的那张。
    const src = target.currentSrc || target.src
    if (!src) return false
    // `noopener` 断掉新标签页对本页 `window.opener` 的引用（图片可能是用户上传的外链）。
    window.open(src, '_blank', 'noopener,noreferrer')
    return true
  }

  const content = (
    <div
      ref={containerRef}
      className={cn('prose-modsite prose-modsite-imglink', className)}
      onClick={(event) => openInNewTab(event.target)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        // 空格键在这里默认会滚动页面，图片被当成可点元素时要拦掉。
        if (openInNewTab(event.target)) event.preventDefault()
      }}
      dangerouslySetInnerHTML={{ __html: rebased }}
    />
  )

  return (
    <>
      {content}
      {/*
        覆盖层挂在容器**内部**那些图片旁边的挂载点上。那些节点不是 React 元素，
        `dangerouslySetInnerHTML` 也不允许同时给 children，因此只能用 createPortal
        把组件送进去。
      */}
      {masks.map(({ url, image, slot }, index) => (
        // 同一张图可以在正文里出现两次，URL 单独当 key 会撞。
        <BodyImageMask key={`${url}#${index}`} url={url} image={image} slot={slot} />
      ))}
    </>
  )
}

/**
 * 正文里一张图的遮罩。
 *
 * 它做两件事：把滤镜类加到那个原生 `<img>` 上（React 管不到它，只能直接改
 * `classList`），以及把覆盖层 portal 进图片旁边的挂载点。「确认显示」之后
 * `masked` 变假，两件事一起撤销。
 */
function BodyImageMask({
  url,
  image,
  slot,
}: {
  url: string
  image: HTMLImageElement
  slot: HTMLElement
}) {
  const masked = useSensitiveMask(url, true)

  useEffect(() => {
    const classes = sensitiveImageClass.split(' ')
    if (!masked) return
    image.classList.add(...classes)
    // 容器级的点击委托据此拦掉「在新标签页打开原图」。
    image.dataset.sensitiveMasked = 'true'
    return () => {
      image.classList.remove(...classes)
      delete image.dataset.sensitiveMasked
    }
  }, [masked, image])

  if (!masked) return null
  return createPortal(<SensitiveOverlay url={url} />, slot)
}
